import { useState } from "react";
import axios from "axios";
import { Send, CheckCircle2, AlertCircle, Loader2, Upload, FileText, X, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UTILITIES = ["Luce", "Gas", "Luce e gas"];
const MAX_BOLLETTA = 10 * 1024 * 1024; // 10 MB

export default function EnergiaPreventivoForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    utility: UTILITIES[2],
    notes: "",
    accept_privacy: false,
  });
  const [bolletta, setBolletta] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const update = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BOLLETTA) {
      setError(`Il file supera i 10 MB.`);
      e.target.value = "";
      return;
    }
    setError("");
    setBolletta(f);
    e.target.value = "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.accept_privacy) {
      setStatus("error");
      setError("Devi spuntare l'accettazione del trattamento dei dati personali per inviare la bolletta.");
      return;
    }
    if (!bolletta) {
      setStatus("error");
      setError("Carica un file della bolletta (PDF o foto).");
      return;
    }
    setStatus("loading");
    setError("");

    const fd = new FormData();
    fd.append("name", form.name.trim());
    fd.append("email", form.email.trim());
    fd.append("phone", form.phone.trim());
    fd.append("utility", form.utility);
    if (form.notes.trim()) fd.append("notes", form.notes.trim());
    fd.append("accept_privacy", "true");
    fd.append("bolletta", bolletta);

    try {
      await axios.post(`${API}/energia/preventivo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStatus("success");
      setForm({
        name: "",
        email: "",
        phone: "",
        utility: UTILITIES[2],
        notes: "",
        accept_privacy: false,
      });
      setBolletta(null);
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Impossibile inviare la richiesta. Riprova più tardi.");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="energia-success" className="bg-white border border-emerald-300 p-10">
        <div className="w-14 h-14 bg-emerald-600 flex items-center justify-center mb-6">
          <CheckCircle2 className="w-7 h-7 text-white" strokeWidth={2} />
        </div>
        <h3 className="font-heading text-2xl lg:text-3xl font-extrabold text-slate-900 mb-4 leading-tight">
          Bolletta ricevuta correttamente.
        </h3>
        <p className="text-slate-600 leading-relaxed mb-8">
          Analizzeremo la tua bolletta e ti contatteremo entro breve con un preventivo
          personalizzato sulle migliori offerte disponibili.
        </p>
        <button onClick={() => setStatus("idle")} data-testid="energia-new-btn" className="btn-outline">
          Invia un'altra richiesta
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="energia-form"
      className="bg-white border border-slate-200 p-8 lg:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
    >
      <div className="sm:col-span-2 flex items-center gap-4 -mt-2 mb-2">
        <div className="w-12 h-12 bg-emerald-600 flex items-center justify-center flex-shrink-0">
          <Zap className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="overline text-emerald-700">Confronto bollette</div>
          <h3 className="font-heading text-xl font-bold text-slate-900 leading-tight">
            Carica la tua bolletta — preventivo gratuito
          </h3>
        </div>
      </div>

      <Field label="Nome e cognome" required>
        <input required minLength={2} maxLength={120} value={form.name} onChange={update("name")} data-testid="energia-name-input" placeholder="Mario Rossi" className="input-90" />
      </Field>
      <Field label="Email" required>
        <input required type="email" value={form.email} onChange={update("email")} data-testid="energia-email-input" placeholder="mario.rossi@email.it" className="input-90" />
      </Field>
      <Field label="Telefono" required>
        <input required type="tel" minLength={5} maxLength={30} value={form.phone} onChange={update("phone")} data-testid="energia-phone-input" placeholder="+39 333 12 34 567" className="input-90" />
      </Field>
      <Field label="Tipo di fornitura" required>
        <select required value={form.utility} onChange={update("utility")} data-testid="energia-utility-select" className="input-90">
          {UTILITIES.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
      </Field>

      <div className="sm:col-span-2">
        <Field label="Note (facoltativo)">
          <textarea maxLength={2000} rows={3} value={form.notes} onChange={update("notes")} data-testid="energia-notes-textarea" placeholder="Es. consumi annui, esigenze particolari, orari preferiti per il contatto..." className="input-90 resize-y min-h-[90px]" />
        </Field>
      </div>

      {/* File upload */}
      <div className="sm:col-span-2">
        <span className="block text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">
          Carica bolletta <span className="text-[color:var(--brand-blue)]">*</span> <span className="text-slate-400 normal-case tracking-normal font-normal">(PDF o foto · max 10 MB)</span>
        </span>
        {!bolletta ? (
          <label
            data-testid="energia-bolletta-label"
            className="flex items-center gap-3 px-5 py-6 bg-emerald-50 border-2 border-dashed border-emerald-300 hover:border-emerald-600 hover:bg-emerald-100 cursor-pointer transition-all"
          >
            <Upload className="w-6 h-6 text-emerald-700" />
            <div className="flex-1">
              <div className="font-heading font-bold text-slate-900">Trascina qui la bolletta</div>
              <div className="text-xs text-slate-600 mt-1">o clicca per selezionarla dal dispositivo</div>
            </div>
            <span className="text-xs text-emerald-700 font-semibold">PDF · JPG · PNG</span>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,application/pdf,image/jpeg,image/png,image/webp,image/heic"
              onChange={onFileChange}
              data-testid="energia-bolletta-input"
              className="hidden"
            />
          </label>
        ) : (
          <div data-testid="energia-bolletta-attached" className="flex items-center gap-3 px-5 py-4 bg-emerald-50 border border-emerald-300">
            <FileText className="w-5 h-5 text-emerald-700 flex-shrink-0" />
            <span className="text-sm font-semibold text-slate-900 flex-1 truncate">{bolletta.name}</span>
            <span className="text-xs text-slate-600">{(bolletta.size / 1024 / 1024).toFixed(2)} MB</span>
            <button type="button" onClick={() => setBolletta(null)} className="text-slate-400 hover:text-red-600" aria-label="Rimuovi">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <label className="sm:col-span-2 flex items-start gap-3 p-4 bg-slate-50 border border-slate-200 cursor-pointer hover:border-emerald-600">
        <input type="checkbox" required checked={form.accept_privacy} onChange={update("accept_privacy")} data-testid="energia-privacy-checkbox" className="mt-1 w-4 h-4 accent-emerald-600" />
        <span className="text-sm text-slate-700 leading-relaxed">
          Ho letto e accetto l'<a href="/reclami#documenti" className="text-emerald-700 font-semibold underline-offset-2 hover:underline">Informativa Privacy</a> ai sensi del Regolamento UE 2016/679 (GDPR) e autorizzo 90 Brokers Srls al trattamento dei miei dati e della bolletta allegata per elaborare un preventivo. <span className="text-emerald-700 font-bold">*</span>
        </span>
      </label>

      <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
        <p className="text-xs text-slate-500 leading-relaxed max-w-md">
          Il preventivo è gratuito e senza impegno. Ti contatteremo entro 48 ore lavorative.
        </p>
        <button
          type="submit"
          disabled={status === "loading"}
          data-testid="energia-submit-btn"
          className="inline-flex items-center gap-2 bg-emerald-600 text-white px-7 py-3.5 font-heading font-semibold tracking-wide hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
        >
          {status === "loading" ? (<><Loader2 className="w-4 h-4 animate-spin" /> Invio in corso…</>) : (<><Send className="w-4 h-4" /> Richiedi preventivo</>)}
        </button>
      </div>

      {status === "error" && error && (
        <div data-testid="energia-error" className="sm:col-span-2 flex items-start gap-3 p-4 bg-red-50 border border-red-200 text-red-800 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">
        {label} {required && <span className="text-emerald-700">*</span>}
      </span>
      {children}
    </label>
  );
}
